/**
 * Client-side GA4 helpers. No-op until gtag bootstrap finishes.
 */

export type GaArticleParams = {
  articleId: number | string
  title: string
  category?: string | null
  path?: string
}

export type GaShareParams = GaArticleParams & {
  method: string
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

export function isGtagReady(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function'
}

/** Wait until gtag is available (bootstrap loads deferred). */
export function whenGtagReady(
  cb: () => void,
  opts?: { timeoutMs?: number; intervalMs?: number },
): () => void {
  const timeoutMs = opts?.timeoutMs ?? 12_000
  const intervalMs = opts?.intervalMs ?? 250
  if (isGtagReady()) {
    cb()
    return () => undefined
  }
  const started = Date.now()
  const timer = window.setInterval(() => {
    if (isGtagReady()) {
      window.clearInterval(timer)
      cb()
      return
    }
    if (Date.now() - started >= timeoutMs) {
      window.clearInterval(timer)
    }
  }, intervalMs)
  return () => window.clearInterval(timer)
}

export function trackGaEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (!isGtagReady()) return
  try {
    window.gtag?.('event', name, params || {})
  } catch {
    /* ignore */
  }
}

/** Dedicated article read signal (also sets content_* for subsequent events). */
export function trackArticleView(input: GaArticleParams): void {
  const contentId = String(input.articleId)
  const payload: Record<string, unknown> = {
    content_id: contentId,
    content_type: 'article',
    content_name: input.title,
  }
  if (input.category) payload.content_category = input.category
  if (input.path) payload.page_path = input.path

  trackGaEvent('article_view', payload)
  try {
    window.gtag?.('set', {
      content_id: contentId,
      content_type: 'article',
      content_name: input.title,
    })
  } catch {
    /* ignore */
  }
}

export function trackShare(input: GaShareParams): void {
  const fire = () => {
    trackGaEvent('share', {
      method: input.method || 'unknown',
      content_id: String(input.articleId),
      content_type: 'article',
      content_name: input.title,
      content_category: input.category || undefined,
    })
  }
  if (isGtagReady()) {
    fire()
    return
  }
  whenGtagReady(fire, { timeoutMs: 8_000 })
}
